import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, Search, Inbox, Users2, FileText,
  Settings, LogOut, ExternalLink, UserCog, Image, Tags, GitCompare, FilePlus2, Compass, LayoutGrid, TrendingUp, Handshake, GraduationCap, PackageSearch, Globe2, FlaskConical, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { ENTRY_MODULES } from "@/lib/perms";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/opportunities", label: "Opportunités", icon: Compass },
  { to: "/admin/projects", label: "Projets", icon: LayoutGrid },
  { to: "/admin/investments", label: "Investissement", icon: TrendingUp },
  { to: "/admin/partners", label: "Partenaires", icon: Handshake },
  { to: "/admin/expert-profiles", label: "Expertise RSTI", icon: GraduationCap },
  { to: "/admin/sourcing", label: "Sourcing", icon: PackageSearch },
  { to: "/admin/markets", label: "Marchés", icon: Globe2 },
  { to: "/admin/market-tests", label: "Market Tests", icon: FlaskConical },
  { to: "/admin/listings", label: "Fiches", icon: ListChecks },
  { to: "/admin/submissions", label: "Soumissions", icon: FilePlus2 },
  { to: "/admin/matching", label: "Matching", icon: GitCompare },
  { to: "/admin/requests", label: "Demandes", icon: Inbox },
  { to: "/admin/suggestions", label: "Suggestions", icon: Search },
  { to: "/admin/experts", label: "Comité RSTI", icon: Users2 },
  { to: "/admin/insights", label: "Analyses", icon: FileText },
  { to: "/admin/media", label: "Médiathèque", icon: Image },
  { to: "/admin/taxonomies", label: "Taxonomies", icon: Tags },
];

const ADMIN_ONLY = [
  { to: "/admin/settings", label: "Paramètres", icon: Settings },
  { to: "/admin/users", label: "Utilisateurs", icon: UserCog },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  useSeo({ title: "Administration RSTI", robots: "noindex,nofollow" });
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const nav = user?.role === "admin" ? [NAV[0], { to: "/admin/access", label: "Gestion des accès", icon: KeyRound }, ...NAV.slice(1), ...ADMIN_ONLY] : NAV.filter((n) => ENTRY_MODULES.includes(n.to) || n.to === "/admin");

  return (
    <div className="flex min-h-screen bg-sand">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-tertiary bg-white lg:flex">
        <div className="border-b border-tertiary px-6 py-5">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-serif text-2xl font-semibold text-anthracite">RSTI</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-champagne">Admin</span>
          </Link>
        </div>
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="admin-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors duration-200 ${
                  isActive ? "bg-anthracite text-ivory" : "text-anthracite/70 hover:bg-sand"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 border-t border-tertiary p-3">
          <a href="/" target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-3 rounded px-3 py-2.5 text-sm text-anthracite/70 hover:bg-sand">
            <ExternalLink className="h-4 w-4" strokeWidth={1.75} /> Voir le site
          </a>
          <button onClick={handleLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-sm text-anthracite/70 hover:bg-sand">
            <LogOut className="h-4 w-4" strokeWidth={1.75} /> Déconnexion
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        <header className="flex items-center justify-between border-b border-tertiary bg-white px-6 py-4">
          <div className="flex gap-2 overflow-x-auto lg:hidden">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap rounded px-3 py-1.5 text-xs ${isActive ? "bg-anthracite text-ivory" : "text-anthracite/60"}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="text-anthracite/60">{user?.email}</span>
            <span className="rounded-full bg-champagne/10 px-2.5 py-1 text-xs font-medium text-champagne">{user?.role}</span>
            <button onClick={handleLogout} className="lg:hidden"><LogOut className="h-4 w-4 text-anthracite/60" /></button>
          </div>
        </header>
        <main className="p-6 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
